import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { teacherApi } from '../../services/api';

// B23 — "Öğrenci Performansı" for the teacher Ödevler screen. Shows performance
// scoped to the teacher's assigned homeworks: an Ödev completion donut (from
// assignment submissions) + an answer-accuracy donut and kazanım strong/weak
// cards (from the scoped students' progress rollups — same tables the student
// "Performansım" donut reads). Filterable by class / subject / student; with no
// filter it shows the general performance across everyone the teacher assigns to.

const BLUE = '#3973bf';
const PINK = '#e5599b';
const GREEN = '#43c95c';
const YELLOW = '#f5c518';
const RED = '#e53e3e';

interface Opt { id: string; name: string }
interface SubjectOpt { id: number; name: string }
interface StudentOpt { uuid: string; name: string; classes: string[] }
interface SubjectCard { subject: string; students: number; accuracy_pct: number; total: number }
interface TopicCard { title: string; subject: string; accuracy_pct: number; wrong_rate: number; total: number }

interface PerfData {
  filters: {
    classes: Opt[];
    subjects: SubjectOpt[];
    students: StudentOpt[];
    applied: { classId: string | null; subjectId: number | null; studentUuid: string | null };
  };
  completion: {
    assignmentCount: number;
    expected: number;
    graded: number;
    pendingReview: number;
    notDelivered: number;
    deliveredPct: number;
    avgScore: number | null;
  };
  accuracy: { correct: number; wrong: number; accuracy_pct: number };
  best3: SubjectCard[];
  worst3: SubjectCard[];
  weakTopics: TopicCard[];
  strongTopics: TopicCard[];
}

// Two-color accuracy ring (Doğru blue over Yanlış pink) — mirrors the student
// Performansım donut so teachers and students read the same visual language.
const AccuracyDonut: React.FC<{ correct: number; wrong: number; avg: number }> = ({ correct, wrong, avg }) => {
  const total = correct + wrong;
  const size = 160;
  const stroke = 20;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const correctFrac = total > 0 ? correct / total : 0;
  const dash = correctFrac * c;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PINK} strokeWidth={stroke} />
          {total > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={BLUE}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeLinecap={correctFrac > 0 && correctFrac < 1 ? 'round' : 'butt'}
            />
          )}
        </g>
        <text x={size / 2} y={size / 2 - 1} textAnchor="middle" fontFamily="Inter" fontSize="30" fontWeight="700" fill="#2b2b2b">%{avg}</text>
        <text x={size / 2} y={size / 2 + 20} textAnchor="middle" fontFamily="Inter" fontSize="12" fontWeight="500" fill="#888">D. Ort</text>
      </svg>
      <div className="flex gap-4 mt-3">
        <Legend color={BLUE} label={`Doğru ${correct}`} />
        <Legend color={PINK} label={`Yanlış ${wrong}`} />
      </div>
    </div>
  );
};

// Three-segment completion ring: Değerlendirildi (green) / Teslim edildi -
// bekliyor (yellow) / Teslim edilmedi (red). Center shows delivered %.
const CompletionDonut: React.FC<{ graded: number; pending: number; notDelivered: number; deliveredPct: number }> = ({ graded, pending, notDelivered, deliveredPct }) => {
  const size = 160;
  const stroke = 20;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = graded + pending + notDelivered;
  const segs = total > 0
    ? [{ v: graded, color: GREEN }, { v: pending, color: YELLOW }, { v: notDelivered, color: RED }]
    : [];
  let offset = 0;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eee" strokeWidth={stroke} />
          {segs.map((s, i) => {
            const len = (s.v / total) * c;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        <text x={size / 2} y={size / 2 - 1} textAnchor="middle" fontFamily="Inter" fontSize="30" fontWeight="700" fill="#2b2b2b">%{deliveredPct}</text>
        <text x={size / 2} y={size / 2 + 20} textAnchor="middle" fontFamily="Inter" fontSize="12" fontWeight="500" fill="#888">Teslim</text>
      </svg>
      <div className="flex flex-wrap justify-center gap-3 mt-3">
        <Legend color={GREEN} label={`Değerlendirildi ${graded}`} />
        <Legend color={YELLOW} label={`Bekliyor ${pending}`} />
        <Legend color={RED} label={`Teslim edilmedi ${notDelivered}`} />
      </div>
    </div>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>{label}</span>
  </div>
);

const StatTile: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <div className="rounded-[12px] px-4 py-3 flex-1 min-w-[120px]" style={{ background: '#f6f7fb' }}>
    <p style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: color ?? '#2b2b2b' }}>{value}</p>
    <p style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: '#888', marginTop: 2 }}>{label}</p>
  </div>
);

const TopicRow: React.FC<{ title: string; subject: string; pct: number; accentColor: string }> = ({ title, subject, pct, accentColor }) => (
  <div className="rounded-[12px] flex items-stretch overflow-hidden" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
    <div className="flex-1 p-2.5 bg-white">
      <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{title}</p>
      <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#888', marginTop: 2 }}>{subject}</p>
    </div>
    <div className="w-[72px] flex flex-col items-center justify-center flex-shrink-0" style={{ background: accentColor }}>
      <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 600, color: '#fff' }}>Doğru</span>
      <span style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 700, color: '#fff' }}>%{pct}</span>
    </div>
  </div>
);

const Select: React.FC<{ value: string; onChange: (v: string) => void; disabled?: boolean; children: React.ReactNode }> = ({ value, onChange, disabled, children }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={`rounded-[12px] px-4 py-2.5 bg-white ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#2b2b2b', border: '1px solid #e5e7eb', minWidth: 160 }}
  >
    {children}
  </select>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-[16px] bg-white p-5" style={{ boxShadow: '0px 1px 6px rgba(0,0,0,0.06)' }}>
    <h2 style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#2b2b2b', marginBottom: 16 }}>{title}</h2>
    {children}
  </div>
);

const TeacherAssignmentPerformance: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PerfData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [studentUuid, setStudentUuid] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    teacherApi
      .assignmentPerformance({
        ...(classId ? { classId } : {}),
        ...(subjectId ? { subjectId } : {}),
        ...(studentUuid ? { studentUuid } : {}),
      })
      .then((res) => {
        if (alive) setData(res.data as PerfData);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.error ?? 'Performans verileri yüklenemedi');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [classId, subjectId, studentUuid]);

  // Changing the class resets the student filter — a student picked under the
  // previous class won't belong to the new one.
  const onClassChange = (v: string) => {
    setClassId(v);
    setStudentUuid('');
  };

  // Students shown in the dropdown = only those enrolled in the selected class.
  const classStudents = useMemo(
    () => (classId && data ? data.filters.students.filter((s) => s.classes.includes(classId)) : []),
    [classId, data],
  );

  const hasFilter = !!(classId || subjectId || studentUuid);
  const scopeLabel = useMemo(() => {
    if (!data) return '';
    const parts: string[] = [];
    if (classId) parts.push(data.filters.classes.find((c) => c.id === classId)?.name ?? '');
    if (subjectId) parts.push(data.filters.subjects.find((s) => String(s.id) === subjectId)?.name ?? '');
    if (studentUuid) parts.push(data.filters.students.find((s) => s.uuid === studentUuid)?.name ?? '');
    return parts.filter(Boolean).join(' · ');
  }, [data, classId, subjectId, studentUuid]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate('/teacher/odevlerim')}
          className="rounded-[12px] w-10 h-10 flex items-center justify-center bg-white hover:bg-gray-50 transition-colors"
          style={{ border: '1px solid #e5e7eb' }}
          aria-label="Geri"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <div>
          <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenci Performansı</h1>
          <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: '#888', marginTop: 2 }}>
            {hasFilter ? scopeLabel : 'Genel performans — atadığınız tüm ödevler'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={classId} onChange={onClassChange}>
          <option value="">Tüm Sınıflar</option>
          {data?.filters.classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select value={subjectId} onChange={setSubjectId}>
          <option value="">Tüm Dersler</option>
          {data?.filters.subjects.map((s) => (
            <option key={s.id} value={String(s.id)}>{s.name}</option>
          ))}
        </Select>
        {/* Student dropdown is scoped to the chosen class and stays disabled
            until one is picked — avoids rendering a full-roster list. */}
        <Select value={studentUuid} onChange={setStudentUuid} disabled={!classId}>
          <option value="">{classId ? 'Tüm Öğrenciler' : 'Önce sınıf seçin'}</option>
          {classStudents.map((s) => (
            <option key={s.uuid} value={s.uuid}>{s.name}</option>
          ))}
        </Select>
        {hasFilter && (
          <button
            onClick={() => { setClassId(''); setSubjectId(''); setStudentUuid(''); }}
            className="rounded-[12px] px-4 py-2.5 hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: PINK, border: `1px solid ${PINK}`, background: '#fff' }}
          >
            Filtreleri Temizle
          </button>
        )}
      </div>

      {loading && (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#888' }}>Yükleniyor…</p>
      )}
      {error && !loading && (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: RED }}>{error}</p>
      )}

      {data && !loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Ödev completion */}
          <Card title="Ödev Tamamlama">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <CompletionDonut
                graded={data.completion.graded}
                pending={data.completion.pendingReview}
                notDelivered={data.completion.notDelivered}
                deliveredPct={data.completion.deliveredPct}
              />
              <div className="flex flex-wrap gap-3 flex-1 w-full">
                <StatTile label="Ödev sayısı" value={data.completion.assignmentCount} />
                <StatTile label="Beklenen teslim" value={data.completion.expected} />
                <StatTile label="Ortalama puan" value={data.completion.avgScore != null ? data.completion.avgScore : '—'} color={BLUE} />
              </div>
            </div>
          </Card>

          {/* Answer accuracy */}
          <Card title="Doğru / Yanlış Oranı">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <AccuracyDonut correct={data.accuracy.correct} wrong={data.accuracy.wrong} avg={data.accuracy.accuracy_pct} />
              <div className="flex flex-wrap gap-3 flex-1 w-full">
                <StatTile label="Doğru" value={data.accuracy.correct} color={BLUE} />
                <StatTile label="Yanlış" value={data.accuracy.wrong} color={PINK} />
                <StatTile label="Çözülen soru" value={data.accuracy.correct + data.accuracy.wrong} />
              </div>
            </div>
            {data.accuracy.correct + data.accuracy.wrong === 0 && (
              <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#888', marginTop: 12 }}>
                Bu kapsamda henüz çözülmüş test verisi yok.
              </p>
            )}
          </Card>

          {/* Best subjects */}
          <Card title="En Başarılı Dersler">
            {data.best3.length ? (
              <div className="flex flex-col gap-2.5">
                {data.best3.map((s, i) => (
                  <TopicRow key={i} title={s.subject} subject={`${s.students} öğrenci`} pct={s.accuracy_pct} accentColor={BLUE} />
                ))}
              </div>
            ) : <Empty />}
          </Card>

          {/* Weakest subjects */}
          <Card title="Gelişime Açık Dersler">
            {data.worst3.length ? (
              <div className="flex flex-col gap-2.5">
                {data.worst3.map((s, i) => (
                  <TopicRow key={i} title={s.subject} subject={`${s.students} öğrenci`} pct={s.accuracy_pct} accentColor={PINK} />
                ))}
              </div>
            ) : <Empty />}
          </Card>

          {/* Strong topics */}
          <Card title="En Başarılı Konular">
            {data.strongTopics.length ? (
              <div className="flex flex-col gap-2.5">
                {data.strongTopics.map((t, i) => (
                  <TopicRow key={i} title={t.title} subject={t.subject} pct={t.accuracy_pct} accentColor={BLUE} />
                ))}
              </div>
            ) : <Empty />}
          </Card>

          {/* Weak topics */}
          <Card title="En Zayıf Konular">
            {data.weakTopics.length ? (
              <div className="flex flex-col gap-2.5">
                {data.weakTopics.map((t, i) => (
                  <TopicRow key={i} title={t.title} subject={t.subject} pct={t.accuracy_pct} accentColor={PINK} />
                ))}
              </div>
            ) : <Empty />}
          </Card>
        </div>
      )}
    </div>
  );
};

const Empty: React.FC = () => (
  <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#888' }}>Bu kapsamda veri yok.</p>
);

export default TeacherAssignmentPerformance;
